import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useAsyncSave } from "@/hooks/useAsyncSave";
import { commitPendingInputs } from "@/utils/commitPendingInputs";

/**
 * Drop-in replacement for a <Button> that submits an async save.
 *
 * Handles:
 *  - Double-submit guarding (duplicate-entry bug class)
 *  - Disabled state during the in-flight save
 *  - Spinner + "Saving…" label
 *  - Safari `<input type="time">` commit safety net: blurs the active
 *    element + yields one microtask before invoking `onSave`, so any
 *    typed-but-not-yet-committed time/date/text value is flushed into
 *    React state first. Eliminates the "I changed PM to AM but Save
 *    didn't take" class of bug.
 *
 * Pass the async handler as `onSave`. Any additional props are forwarded
 * to the underlying shadcn Button (variant, size, className, data-testid…).
 *
 * When you also need to disable surrounding inputs while saving, use the
 * `useAsyncSave` hook directly and wire its `saving` to your inputs.
 */
export function AsyncSaveButton({
  onSave,
  children = "Save",
  savingLabel = "Saving…",
  className = "",
  disabled = false,
  ...rest
}) {
  const { saving, run } = useAsyncSave();

  const handleClick = () => run(async () => {
    await commitPendingInputs();
    await onSave?.();
  });

  return (
    <Button
      {...rest}
      disabled={saving || disabled}
      onClick={handleClick}
      className={`${className} disabled:cursor-not-allowed`.trim()}
    >
      {saving ? (
        <span className="inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          {savingLabel}
        </span>
      ) : (
        children
      )}
    </Button>
  );
}

export default AsyncSaveButton;
